/* ==========================================================================
   Consent gate for third-party embeds in theme code.

   Shopify's cookie banner (Settings > Customer privacy) only controls
   Shopify's own tracking and scripts that ask the Customer Privacy API.
   Embeds written into this theme wait here for the visitor's choice:

   - [data-consent-embed]        A box with a static fallback and a
       <template data-consent-content>. Once the purpose named in
       data-consent is allowed, the template replaces the fallback and the
       script in data-consent-script loads once (Trustpilot TrustBoxes).
       With data-consent-defer as well, that waits for the visitor's first
       scroll, tap or key press, or 5 seconds after load.
   - img[data-consent-src]       A third-party image held back until the
       purpose is allowed (YouTube thumbnails, when Theme settings >
       Privacy & embeds says "Only after consent"; by default they show
       straight away, as an image they set no cookies).
   - [data-video-facade]         Click-to-play YouTube / Vimeo. Nothing loads
       until the visitor presses play. That click is their request for the
       video, so it needs no cookie consent; it plays from
       youtube-nocookie.com (Vimeo with dnt=1).
   - template[data-embed-iframe] Iframes pasted into article copy, made inert
       in Liquid. YouTube / Vimeo become facades; anything else gets a
       "Show content" button.
   - [data-cookie-preferences]   Footer link that reopens the banner's
       preferences panel. Stays hidden where Shopify's banner isn't running.

   The choice is re-read whenever the visitor changes it
   (visitorConsentCollected), so a TrustBox appears straight after Accept
   without a reload. In the theme editor everything shows.
   ========================================================================== */
(function () {
  'use strict';

  var scripts = {};

  function designMode() {
    return !!(window.Shopify && window.Shopify.designMode);
  }

  function defaultPurpose() {
    return (window.theme && window.theme.embedConsent) || 'marketing';
  }

  /* True when the visitor allows this purpose. Before the Customer Privacy
     API has loaded we can't know, so nothing third-party loads. */
  function allowed(purpose) {
    if (designMode()) return true;
    var cp = window.Shopify && window.Shopify.customerPrivacy;
    if (!cp) return false;
    try {
      if (purpose === 'preferences') return !!cp.preferencesProcessingAllowed();
      if (purpose === 'analytics') return !!cp.analyticsProcessingAllowed();
      return !!cp.marketingAllowed();
    } catch (e) {
      return false;
    }
  }

  /* Load a script once. The callback gets true when the script was already
     on the page before this call. */
  function loadScript(src, done) {
    var entry = scripts[src];
    if (entry === 'loaded') { done(true); return; }
    if (entry) { entry.push(done); return; }
    scripts[src] = [done];
    var s = document.createElement('script');
    s.src = src;
    s.async = true;
    s.onload = function () {
      var queue = scripts[src];
      scripts[src] = 'loaded';
      queue.forEach(function (fn) { fn(false); });
    };
    document.head.appendChild(s);
  }

  function activateEmbed(box) {
    var tpl = box.querySelector('template[data-consent-content]');
    if (!tpl) return;
    box.setAttribute('data-consent-state', 'on');
    Array.prototype.slice.call(box.children).forEach(function (child) {
      if (child !== tpl) child.remove();
    });
    box.appendChild(tpl.content.cloneNode(true));
    tpl.remove();

    var src = box.getAttribute('data-consent-script');
    if (!src) return;
    loadScript(src, function (wasLoaded) {
      /* The TrustBox script scans the page when it first loads. Widgets
         added after that need an explicit call. */
      if (!wasLoaded) return;
      if (window.Trustpilot && typeof window.Trustpilot.loadFromElement === 'function') {
        box.querySelectorAll('.trustpilot-widget').forEach(function (el) {
          window.Trustpilot.loadFromElement(el);
        });
      }
    });
  }

  /* ---------- Load after first interaction ---------- */

  /* Boxes marked data-consent-defer wait until the visitor first scrolls,
     taps or presses a key, or 5 seconds after the page has loaded,
     whichever comes first, so their script stays out of the first load.
     The fallback line holds the box's height meanwhile, so nothing moves. */
  var awake = false;
  var waiting = [];
  var wakeEvents = ['scroll', 'pointerdown', 'keydown'];

  function wake() {
    if (awake) return;
    awake = true;
    wakeEvents.forEach(function (name) {
      window.removeEventListener(name, wake, { passive: true });
    });
    waiting.splice(0).forEach(function (fn) { fn(); });
  }

  function afterInteraction(fn) {
    if (awake || designMode()) { fn(); return; }
    waiting.push(fn);
  }

  wakeEvents.forEach(function (name) {
    window.addEventListener(name, wake, { passive: true });
  });
  function wakeLater() { setTimeout(wake, 5000); }
  if (document.readyState === 'complete') wakeLater();
  else window.addEventListener('load', wakeLater);

  function apply() {
    document.querySelectorAll('[data-consent-embed]:not([data-consent-state])').forEach(function (box) {
      if (!allowed(box.getAttribute('data-consent') || defaultPurpose())) return;
      if (box.hasAttribute('data-consent-defer')) {
        box.setAttribute('data-consent-state', 'waiting');
        afterInteraction(function () { activateEmbed(box); });
        return;
      }
      activateEmbed(box);
    });
    document.querySelectorAll('img[data-consent-src]').forEach(function (img) {
      if (!allowed(img.getAttribute('data-consent') || defaultPurpose())) return;
      sharpen(img);
      img.src = img.getAttribute('data-consent-src');
      img.removeAttribute('data-consent-src');
      img.hidden = false;
    });
  }

  /* YouTube's hqdefault thumbnail (480px) always exists but looks soft on
     a wide panel. Once it has loaded, if the panel is wider than 480px,
     try the full-HD thumbnail and swap it in only if the video has one
     (a missing one comes back as a 120px placeholder). */
  function sharpen(img) {
    var id = img.getAttribute('data-yt-id');
    if (!id || img.getAttribute('data-sharp')) return;
    img.setAttribute('data-sharp', '1');
    function run() {
      if (img.getBoundingClientRect().width <= 480) return;
      var probe = new Image();
      probe.onload = function () {
        if (probe.naturalWidth >= 1280) img.src = probe.src;
      };
      probe.src = 'https://i.ytimg.com/vi/' + id + '/maxresdefault.jpg';
    }
    if (img.complete && img.naturalWidth) run();
    else img.addEventListener('load', run, { once: true });
  }

  /* ---------- Click-to-play video ---------- */

  function videoEmbed(src) {
    var yt = src.match(/(?:youtube(?:-nocookie)?\.com\/(?:embed\/|watch\?v=|shorts\/)|youtu\.be\/)([\w-]{11})/);
    if (yt) {
      return {
        host: 'YouTube',
        id: yt[1],
        url: 'https://www.youtube-nocookie.com/embed/' + yt[1] + '?autoplay=1&rel=0'
      };
    }
    var vm = src.match(/(?:player\.)?vimeo\.com\/(?:video\/)?(\d+)/);
    if (vm) {
      return {
        host: 'Vimeo',
        id: vm[1],
        url: 'https://player.vimeo.com/video/' + vm[1] + '?autoplay=1&dnt=1'
      };
    }
    return null;
  }

  function el(tag, cls, text) {
    var node = document.createElement(tag);
    if (cls) node.className = cls;
    if (text) node.textContent = text;
    return node;
  }

  /* Same markup as snippets/video-facade.liquid, for iframes found in copy. */
  function buildFacade(v, title) {
    var box = el('div', 'video-facade');
    box.setAttribute('data-video-facade', '');
    box.setAttribute('data-embed', v.url);
    box.setAttribute('data-title', title);

    var btn = el('button', 'video-facade__btn');
    btn.type = 'button';
    btn.setAttribute('aria-label', 'Play video: ' + title + ' (plays a ' + v.host + ' video)');

    if (v.host === 'YouTube') {
      var img = el('img', 'video-facade__img');
      var thumb = 'https://i.ytimg.com/vi/' + v.id + '/hqdefault.jpg';
      img.alt = '';
      img.loading = 'lazy';
      img.width = 480;
      img.height = 360;
      img.setAttribute('data-yt-id', v.id);
      if (window.theme && window.theme.ytThumbs === 'consent') {
        img.hidden = true;
        img.setAttribute('data-consent-src', thumb);
        img.setAttribute('data-consent', defaultPurpose());
      } else {
        sharpen(img);
        img.src = thumb;
      }
      btn.appendChild(img);
    }
    var shade = el('span', 'video-facade__shade');
    shade.setAttribute('aria-hidden', 'true');
    var play = el('span', 'video-facade__play');
    play.setAttribute('aria-hidden', 'true');
    play.innerHTML = '<svg width="22" height="22" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true"><path d="M5 3v10l8-5z"/></svg>';
    var meta = el('span', 'video-facade__meta');
    meta.setAttribute('aria-hidden', 'true');
    meta.appendChild(el('span', 'video-facade__title', title));
    meta.appendChild(el('span', 'video-facade__note', 'Plays a ' + v.host + ' video'));
    btn.appendChild(shade);
    btn.appendChild(play);
    btn.appendChild(meta);
    box.appendChild(btn);
    return box;
  }

  function playFacade(box) {
    var src = box.getAttribute('data-embed');
    if (!src) return;
    var frame = document.createElement('iframe');
    frame.className = 'video-facade__frame';
    frame.src = src;
    frame.title = box.getAttribute('data-title') || 'Video';
    frame.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
    frame.setAttribute('allowfullscreen', '');
    frame.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');
    var btn = box.querySelector('.video-facade__btn');
    if (btn) btn.replaceWith(frame); else box.appendChild(frame);
    box.classList.add('is-playing');
    frame.focus();
  }

  /* Iframes pasted into article copy arrive inside
     <template data-embed-iframe> so the browser never loads them. */
  function convertInlineIframes() {
    document.querySelectorAll('template[data-embed-iframe]').forEach(function (tpl) {
      var frame = tpl.content.querySelector('iframe');
      if (!frame) { tpl.remove(); return; }
      var src = frame.getAttribute('src') || frame.getAttribute('data-src') || '';
      var title = frame.getAttribute('title') || 'Video';
      var v = videoEmbed(src);
      if (v) {
        /* Old embed code wraps the iframe in a padding-bottom: 56.25% box
           with height 0. The facade sizes itself 16:9, so drop that. */
        var wrap = tpl.parentElement;
        if (wrap && wrap.style && wrap.style.paddingBottom) wrap.removeAttribute('style');
        tpl.replaceWith(buildFacade(v, title));
        return;
      }
      /* Anything else: a button that loads it on request. */
      var host = '';
      try { host = new URL(src, location.href).hostname; } catch (e) { host = ''; }
      var holder = el('div', 'embed-blocked');
      holder.appendChild(el('p', 'embed-blocked__text', 'Embedded content' + (host ? ' from ' + host : '') + '.'));
      var show = el('button', 'embed-blocked__btn', 'Show content');
      show.type = 'button';
      show.addEventListener('click', function () {
        holder.replaceWith(frame);
      });
      holder.appendChild(show);
      tpl.replaceWith(holder);
    });
  }

  /* ---------- Cookie preferences link ---------- */

  function bannerReady() {
    return !!(window.privacyBanner && typeof window.privacyBanner.showPreferences === 'function');
  }

  function revealPreferenceLinks() {
    if (!bannerReady()) return false;
    document.querySelectorAll('[data-cookie-preferences]').forEach(function (link) {
      link.hidden = false;
    });
    return true;
  }

  /* Shopify's banner script can arrive after this file, so look a few times. */
  function watchForBanner() {
    var tries = 0;
    (function check() {
      if (revealPreferenceLinks() || ++tries > 12) return;
      setTimeout(check, 500);
    })();
  }

  document.addEventListener('click', function (e) {
    var play = e.target.closest('[data-video-facade] .video-facade__btn');
    if (play) {
      e.preventDefault();
      playFacade(play.closest('[data-video-facade]'));
      return;
    }
    var prefs = e.target.closest('[data-cookie-preferences]');
    if (prefs && bannerReady()) {
      e.preventDefault();
      window.privacyBanner.showPreferences();
    }
  });

  function init() {
    convertInlineIframes();
    document.querySelectorAll('img[data-yt-id][src]').forEach(sharpen);
    apply();
    if (!designMode() && window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function (error) {
        if (!error) apply();
      });
    }
    document.addEventListener('visitorConsentCollected', apply);
    document.addEventListener('shopify:section:load', function () {
      convertInlineIframes();
      apply();
    });
    watchForBanner();
    window.fpsConsent = { allowed: allowed, refresh: apply };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
